<template>
  <SideModal
    :title="title"
    @close="$emit('close')"
    :show="showAcknowledgementModal"
    class="acknowledgment-modal"
    @go-back="$emit('close')"
  >
    <template v-slot:content>
      <div class="acknowledgment-content">
        <!-- Employee Profile Card -->
        <div class="profile-card">
          <div class="profile-avatar">
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M14 14C16.8995 14 19.25 11.6495 19.25 8.75C19.25 5.85051 16.8995 3.5 14 3.5C11.1005 3.5 8.75 5.85051 8.75 8.75C8.75 11.6495 11.1005 14 14 14Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M23.0182 24.5C23.0182 20.2475 18.9782 16.8 14.0002 16.8C9.02215 16.8 4.98215 20.2475 4.98215 24.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <div class="profile-info">
            <h3 class="profile-name">{{ inviteInfo.name }}</h3>
            <p class="profile-email">{{ inviteInfo.email }}</p>
            <span class="profile-role">{{ inviteInfo.role }}</span>
          </div>
        </div>

        <!-- Details Grid -->
        <div class="details-card">
          <h4 class="details-title">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 16.5C13.1421 16.5 16.5 13.1421 16.5 9C16.5 4.85786 13.1421 1.5 9 1.5C4.85786 1.5 1.5 4.85786 1.5 9C1.5 13.1421 4.85786 16.5 9 16.5Z" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M9 4.5V9L12 10.5" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            Vesting Details
          </h4>
          
          <div class="details-grid">
            <div class="detail-item">
              <span class="detail-label">Vesting Period</span>
              <span class="detail-value">{{ inviteInfo.vesting_duration }} Years</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Vesting Frequency</span>
              <span class="detail-value">{{ frequency(inviteInfo.vesting_frequency) }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Allocated Shares</span>
              <span class="detail-value">{{ convertToLocale(Number(inviteInfo.allocated_shares)) }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Cliff Period</span>
              <span class="detail-value">{{ convertToLocale(Number(inviteInfo.cliff_period)) }} Months</span>
            </div>
            <template v-if="inviteInfo.employee_type === 'ESOP_EMPLOYEE'">
              <div class="detail-item">
                <span class="detail-label">Strike Price</span>
                <span class="detail-value">{{ formatAmountToDollar(inviteInfo.strike_price, 2, "USD") }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Exercise Timeline</span>
                <span class="detail-value">{{ convertToLocale(Number(inviteInfo.exercise_timeline)) }} Months</span>
              </div>
            </template>
            <div class="detail-item">
              <span class="detail-label">Market Price</span>
              <span class="detail-value highlight">{{ formatAmountToDollar(marketPrice, 2, "USD") }}</span>
            </div>
          </div>
        </div>

        <!-- Documents Section -->
        <div class="documents-section" v-if="inviteInfo.documents && inviteInfo.documents.length">
          <h4 class="documents-title">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15.75 7.5V11.25C15.75 15 14.25 16.5 10.5 16.5H7.5C3.75 16.5 2.25 15 2.25 11.25V6.75C2.25 3 3.75 1.5 7.5 1.5H11.25" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M15.75 7.5H12.75C10.5 7.5 9.75 6.75 9.75 4.5V1.5L15.75 7.5Z" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            Legal Documents
          </h4>
          <div class="documents-grid">
            <a 
              v-for="(document, index) in inviteInfo.documents" 
              :key="index"
              :href="document.url" 
              target="_blank" 
              rel="noopener noreferrer"
              class="document-card"
            >
              <div class="document-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M22 10V15C22 20 20 22 15 22H9C4 22 2 20 2 15V9C2 4 4 2 9 2H14" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M22 10H18C15 10 14 9 14 6V2L22 10Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
              <div class="document-info">
                <span class="document-name">{{ document.name }}</span>
                <span class="document-type">PDF Document</span>
              </div>
              <div class="document-action">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M6 12L10 8L6 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
            </a>
          </div>
        </div>

        <!-- Confirmation Form -->
        <div class="confirmation-section">
          <div class="confirmation-header">
            <h4 class="confirmation-title">Confirm Invitation</h4>
            <p class="confirmation-description">
              You are inviting the above employee to receive shares. Confirm this by entering your organization name.
            </p>
          </div>
          
          <div class="start-date-badge">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M5.33333 1.33334V3.33334" stroke="currentColor" stroke-width="1.2" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M10.6667 1.33334V3.33334" stroke="currentColor" stroke-width="1.2" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M2.33333 6.06H13.6667" stroke="currentColor" stroke-width="1.2" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M14 5.66667V11.3333C14 13.3333 13 14.6667 10.6667 14.6667H5.33333C3 14.6667 2 13.3333 2 11.3333V5.66667C2 3.66667 3 2.33334 5.33333 2.33334H10.6667C13 2.33334 14 3.66667 14 5.66667Z" stroke="currentColor" stroke-width="1.2" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M10.4631 9.13333H10.4691" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M10.4631 11.1333H10.4691" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M7.99715 9.13333H8.00314" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M7.99715 11.1333H8.00314" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M5.52954 9.13333H5.53552" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M5.52954 11.1333H5.53552" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            Start Date: {{ startDate }}
          </div>

          <form @submit.prevent="checkFormValidation" class="confirmation-form">
            <InputField
              :validate="validateForm"
              label="Organisation Name"
              type="text"
              name="acknowledgement_name"
              placeholder="Enter your organization name"
              :sameAs="userInfo.name"
              @input-changed="storeValue"
              @is-valid="handleValidation"
            />
            
            <button type="submit" class="confirm-btn" :disabled="loading">
              <span v-if="!loading">Confirm & Send Invite</span>
              <span v-else class="loading-state">
                <svg class="spinner" width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 1.5V4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M9 13.5V16.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" opacity="0.4"/>
                  <path d="M3.69727 3.69751L5.81977 5.82001" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M12.1802 12.18L14.3027 14.3025" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" opacity="0.4"/>
                  <path d="M1.5 9H4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M13.5 9H16.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" opacity="0.4"/>
                  <path d="M3.69727 14.3025L5.81977 12.18" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M12.1802 5.82001L14.3027 3.69751" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" opacity="0.4"/>
                </svg>
                Processing...
              </span>
            </button>
          </form>
        </div>
      </div>
    </template>
  </SideModal>
</template>

<script>
import InputField from "@/components/general/InputField";
import CustomButton from "@/components/general/BtnComponent";
import {
  convertToLocale,
  formatAmountToDollar,
  formatISODate,
} from "@/utils/helpers";
import SideModal from "@/components/general/sidemodal/SideModal";
import { getItem } from "@/utils/storage";
import { mapGetters, mapState } from "vuex";

export default {
  name: "Acknowledgment",
  
  data() {
    return {
      acknowledgement_name: "",
      validationResult: [],
      showDefaultDocument: false,
      showAcknowledgementModal: false,
      validateForm: false,
      startDate: "",
      title: "",
      months: [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ],
    };
  },
  
  components: {
    InputField,
    CustomButton,
    SideModal,
  },
  
  props: {
    inviteInfo: {
      type: Object,
      required: true,
    },
    show: {
      type: Boolean,
      required: true,
    },
  },
  
  emits: ["send-invite", "close"],
  
  watch: {
    inviteInfo(val) {
      if (val) {
        this.showAcknowledgementModal = true;
      }
    },
    validationResult: {
      deep: true,
      handler(val) {
        if (this.show && val.length === 1 && !val.includes(false)) {
          this.$emit("send-invite", this.acknowledgement_name);
        }
      },
    },
  },
  
  computed: {
    ...mapGetters("Esop", ["esopToken"]),
    ...mapState({
      loading: (state) => state.loading,
    }),
    userInfo() {
      return getItem("org_details");
    },
    marketPrice() {
      return this.esopToken.valuation / this.esopToken.total_supply;
    },
  },
  
  mounted() {
    this.setModalInfo();
  },
  
  methods: {
    convertToLocale,
    formatAmountToDollar,
    formatISODate,
    getItem,
    
    setModalInfo() {
      let startDate = new Date(this.inviteInfo.start_date);
      let month = "";
      let day = "";
      let year = "";
      let type = "";
      month = this.months[startDate.getMonth()];
      day = startDate.getDate();
      year = startDate.getFullYear();
      this.startDate = `${month} ${day}, ${year}`;
      type = this.inviteInfo.employee_type === "ESOP_EMPLOYEE" ? "ESO" : "RSU";
      this.title = `Employee Invitation for ${type}`;
    },
    
    handleValidation(value) {
      this.validationResult.push(value);
    },
    
    storeValue(object) {
      this[object.name] = object.value;
    },
    
    checkFormValidation() {
      this.validationResult = [];
      this.validateForm = true;
      setTimeout(() => {
        this.validateForm = false;
      }, 1000);
    },
    
    frequency(value) {
      let frequency = null;
      switch (value) {
        case "Daily":
          frequency = 1;
          break;
        case 1:
          frequency = "Daily";
          break;
        case 30:
          frequency = "Monthly";
          break;
        case "Monthly":
          frequency = 30;
          break;
        case 91:
          frequency = "Quarterly";
          break;
        case "Quarterly":
          frequency = 91;
          break;
        case 182:
          frequency = "Bi-annually";
          break;
        case "Bi-annually":
          frequency = 182;
          break;
        case 365:
          frequency = "Yearly";
          break;
        case "Yearly":
          frequency = 365;
          break;
      }
      return frequency;
    },
  },
};
</script>

<style lang="scss" scoped>
.acknowledgment-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 0 8px;
}

.profile-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px;
  background: linear-gradient(135deg, #EEF2FF 0%, #F8FAFC 100%);
  border: 1px solid #E2E8F0;
  border-radius: 12px;
}

.profile-avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  background: white;
  border: 1px solid #E2E8F0;
  border-radius: 50%;
  color: #4D84FF;
  flex-shrink: 0;
}

.profile-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.profile-name {
  font-family: 'DM Sans', sans-serif;
  font-size: 16px;
  font-weight: 500;
  color: #0F172A;
  margin: 0;
}

.profile-email {
  font-family: 'DM Sans', sans-serif;
  font-size: 13px;
  color: #64748B;
  margin: 0;
}

.profile-role {
  display: inline-block;
  padding: 4px 10px;
  background: white;
  border: 1px solid #E2E8F0;
  border-radius: 6px;
  font-family: 'DM Sans', sans-serif;
  font-size: 12px;
  color: #4D84FF;
  margin-top: 4px;
  width: fit-content;
}

.details-card {
  background: white;
  border: 1px solid #E2E8F0;
  border-radius: 12px;
  padding: 20px;
}

.details-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: 'DM Sans', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #0F172A;
  margin: 0 0 16px 0;
  padding-bottom: 12px;
  border-bottom: 1px solid #F1F5F9;
  
  svg {
    color: #4D84FF;
  }
}

.details-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px;
  background: #F8FAFC;
  border-radius: 8px;
}

.detail-label {
  font-family: 'DM Sans', sans-serif;
  font-size: 12px;
  color: #64748B;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.detail-value {
  font-family: 'DM Sans', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #0F172A;
  
  &.highlight {
    color: #4D84FF;
  }
}

.documents-section {
  background: white;
  border: 1px solid #E2E8F0;
  border-radius: 12px;
  padding: 20px;
}

.documents-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: 'DM Sans', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #0F172A;
  margin: 0 0 16px 0;
  padding-bottom: 12px;
  border-bottom: 1px solid #F1F5F9;
  
  svg {
    color: #4D84FF;
  }
}

.documents-grid {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.document-card {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
  border-radius: 10px;
  text-decoration: none;
  transition: all 0.2s ease;
  
  &:hover {
    background: #EEF2FF;
    border-color: #4D84FF;
    
    .document-action {
      color: #4D84FF;
    }
  }
}

.document-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: white;
  border: 1px solid #E2E8F0;
  border-radius: 8px;
  color: #4D84FF;
  flex-shrink: 0;
}

.document-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
}

.document-name {
  font-family: 'DM Sans', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #0F172A;
}

.document-type {
  font-family: 'DM Sans', sans-serif;
  font-size: 12px;
  color: #64748B;
}

.document-action {
  color: #94A3B8;
  transition: color 0.2s ease;
}

.confirmation-section {
  background: white;
  border: 1px solid #E2E8F0;
  border-radius: 12px;
  padding: 20px;
}

.confirmation-header {
  margin-bottom: 16px;
}

.confirmation-title {
  font-family: 'DM Sans', sans-serif;
  font-size: 16px;
  font-weight: 500;
  color: #0F172A;
  margin: 0 0 6px 0;
}

.confirmation-description {
  font-family: 'DM Sans', sans-serif;
  font-size: 13px;
  color: #64748B;
  margin: 0;
  line-height: 1.5;
}

.start-date-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: #EEF2FF;
  border-radius: 8px;
  font-family: 'DM Sans', sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: #4D84FF;
  margin-bottom: 20px;
}

.confirmation-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.confirm-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 14px 24px;
  background: #4D84FF;
  border: none;
  border-radius: 8px;
  color: white;
  font-family: 'DM Sans', sans-serif;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 100%;

  &:hover:not(:disabled) {
    background: #3B6FE8;
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
}

.loading-state {
  display: flex;
  align-items: center;
  gap: 8px;
}

.spinner {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
